import { useCallback, useEffect, useRef, useState } from 'react';
import { useCreateFormAssetUpload } from '../../gql';
import { uploadToTicket } from '../../shared/upload';

// Uploads a logo / section banner (build plan §4.2): ask for a presigned
// POST (createFormAssetUpload), send the file straight to S3, then keep a
// local object URL so the canvas shows it before the form is saved.
//
// upload(file) → { assetId } | { error }. The caller puts assetId in the
// field's config; saving the form attaches it.
//
// Every object URL this hook makes is revoked when the builder closes,
// including ones a Reload dropped from state. An upload that finishes after
// that does nothing more.

export const ASSET_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const ASSET_MAX_BYTES = 5 * 1024 * 1024;
export const ASSET_ACCEPT = ASSET_TYPES.join(',');

export const checkAssetFile = (file) => {
  if (!file) return 'Choose an image';
  if (!ASSET_TYPES.includes(file.type)) return 'Use a JPG, PNG or WebP image';
  if (!file.size || file.size > ASSET_MAX_BYTES)
    return 'Images can be up to 5 MB';
  return null;
};

const objectUrl = (file) => {
  try {
    return window.URL && window.URL.createObjectURL
      ? window.URL.createObjectURL(file)
      : null;
  } catch (e) {
    return null;
  }
};

const revokeUrl = (url) => {
  try {
    if (window.URL && window.URL.revokeObjectURL)
      window.URL.revokeObjectURL(url);
  } catch (e) {
    // Already gone; nothing to free.
  }
};

const GONE = { error: null };

const useAssetUpload = (formId, dispatch) => {
  const createTicket = useCreateFormAssetUpload();
  const [busyCount, setBusyCount] = useState(0);
  const alive = useRef(true);
  const urls = useRef(new Set());

  useEffect(() => {
    alive.current = true;
    const made = urls.current;
    return () => {
      alive.current = false;
      made.forEach(revokeUrl);
      made.clear();
    };
  }, []);

  const send = useCallback(
    async (file) => {
      const { data, error } = await createTicket({
        form_id: formId,
        filename: file.name,
        content_type: file.type,
        size_bytes: file.size,
      });
      if (error) return { error: error.global };
      if (!alive.current) return GONE;
      const sent = await uploadToTicket(data, file);
      if (!sent.ok) return { error: sent.message };
      if (!alive.current) return GONE;
      const url = objectUrl(file);
      if (url) {
        urls.current.add(url);
        dispatch({ type: 'SET_LOCAL_ASSET', assetId: data.file_id, url });
      }
      return { assetId: data.file_id };
    },
    [createTicket, dispatch, formId]
  );

  const upload = useCallback(
    async (file) => {
      const problem = checkAssetFile(file);
      if (problem) return { error: problem };
      setBusyCount((n) => n + 1);
      try {
        const result = await send(file);
        return alive.current ? result : GONE;
      } finally {
        if (alive.current) setBusyCount((n) => n - 1);
      }
    },
    [send]
  );

  return { upload, busy: busyCount > 0 };
};

export default useAssetUpload;
